// React
import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Components
import UspMarketingHeader from './usp-header';
import GetInTouchSection from './get-in-touch';
import {
  Banner,
  Feature,
  Overview,
  MinimalFooter,
  MarketplaceFeatureSection,
  PartnerOverview,
  CustomNotificationBar
} from '@mobilitystore/components-interface';

// Actions
import { MPS_BASE_URL } from '../../actions/configuration/index';

// Assets
import { CDN_LINK } from '../../assets/img';

// Utils
import { getDeviceClass, formatMessage } from '../../utils';
import Loader from '../Shared/Utils/Loader';

// Data

// Css
import './index.scss';

const Landing = () => {
  const history = useHistory();
  const { content, isLoading } = useSelector((state) => ({
    content: state.ContentReducer.content,
    isLoading: state.OrderSummaryReducer?.isLoading
  }));

  const landingContent = content?.components?.landing;
  const footerContent = content?.components?.footer;
  const [scrollToTop, setScrollToTop] = useState(false);
  const [updatedFooterTabs, setUpdatedFooterTabs] = useState([]);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);

  const currentYear = new Date().getFullYear();
  const entityName = formatMessage(footerContent?.name, currentYear);
  const headerScrollOffset =
    parseFloat(getComputedStyle(document.documentElement).fontSize) * 12;
  const deviceClass = getDeviceClass();
  const aboutUsRef = useRef(null);
  const ourFeaturesRef = useRef(null);
  const contactUsRef = useRef(null);
  const partnersRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollToTop(window.scrollY > 200);
      setIsHeaderSticky(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (footerContent?.menu) {
      const newFooterTab = {
        id: '5',
        name: 'Privacy settings',
        onClick: () => showDockPrivacySettings()
      };
      const updatedTabs = [...footerContent.menu, newFooterTab];
      setUpdatedFooterTabs(updatedTabs);
    }
  }, [footerContent]);

  async function showDockPrivacySettings() {
    await customElements.whenDefined('dock-privacy-settings');
    const dockPrivacySettings = document.querySelector('dock-privacy-settings');
    dockPrivacySettings.setAttribute('visible', 'true');
  }

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUspButtonClick = () => {
    history.push('/login');
  };

  const handleMpsButtonClick = () => {
    window.open(MPS_BASE_URL, '_blank');
  };

  const handleMenuClick = (item) => {
    const refMapping = {
      1: aboutUsRef,
      2: ourFeaturesRef,
      3: partnersRef,
      4: contactUsRef
    };

    const targetRef = refMapping[item.id];
    if (targetRef?.current) {
      const top =
        targetRef.current.getBoundingClientRect().top +
        window.scrollY -
        headerScrollOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleScrollToContactUs = () => {
    if (contactUsRef.current) {
      const top =
        contactUsRef.current.getBoundingClientRect().top +
        window.scrollY -
        headerScrollOffset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className={`usp-landing  ${deviceClass}`}>
      {isLoading && <Loader />}
      <div className={`usp-header ${isHeaderSticky ? 'sticky' : ''}`}>
        <UspMarketingHeader
          handleMenuClick={handleMenuClick}
          handleGetInTouchClick={handleScrollToContactUs}
        />
      </div>

      <CustomNotificationBar
        notifications={[landingContent?.notification]}
        marquee={true}
      />

      <div className='usp-banner'>
        <Banner
          id='1'
          className='usp-banner__container'
          type={landingContent?.banner?.type}
          media={{
            video: `${CDN_LINK}/seller/${landingContent?.banner?.media?.video}`
          }}
        />
      </div>

      <Feature
        className='digital-disappear__feature'
        overview={{
          header: landingContent?.digital_disappear?.title
        }}
      />
      <Overview
        id='2'
        className='digital-disappear__overview'
        header={landingContent?.digital_disappear?.overview?.title}
        description={landingContent?.digital_disappear?.overview?.description}
        media={{
          text: {
            className: 'media-overview',
            header: landingContent?.digital_disappear?.overview?.media?.title
          }
        }}
      />

      <Overview
        id='3'
        ref={aboutUsRef}
        className='usp-overview'
        header={landingContent?.usp_about_us?.title}
        description={landingContent?.usp_about_us?.description}
        media={{
          image: `${CDN_LINK}/seller/${landingContent?.usp_about_us?.media?.image}`,
          video: `${CDN_LINK}/seller/${landingContent?.usp_about_us?.media?.video}`
        }}
        button={{
          type: landingContent?.usp_about_us?.button?.type,
          name: landingContent?.usp_about_us?.button?.name,
          handleButtonClick: () => handleUspButtonClick()
        }}
      />

      <Feature
        className='usp-marketplace-feature'
        overview={{
          header: landingContent?.marketplace_expansion?.title
        }}
        features={landingContent?.marketplace_expansion?.features?.map(
          (feature) => ({
            ...feature,
            className: 'usp-marketplace-feature__list'
          })
        )}
      />

      <h2 ref={ourFeaturesRef} className='feature-container'>
        {landingContent?.usp_features?.title}
      </h2>
      <MarketplaceFeatureSection
        id='4'
        className='digitization-feature '
        featureOverview={{
          className: '',
          label: landingContent?.usp_features?.digitization?.title,
          header: landingContent?.usp_features?.digitization?.header,
          content: landingContent?.usp_features?.digitization?.content
        }}
        mediaOverview={{
          id: landingContent?.usp_features?.digitization?.media?.id,
          className: '',
          media: {
            image: `${CDN_LINK}/seller/${landingContent?.usp_features?.digitization?.media?.image}`
          }
        }}
      />

      <MarketplaceFeatureSection
        id='5'
        className='centralized-management-feature '
        featureOverview={{
          className: '',
          label: landingContent?.usp_features?.centralized_management?.title,
          header: landingContent?.usp_features?.centralized_management?.header,
          content: landingContent?.usp_features?.centralized_management?.content
        }}
        mediaOverview={{
          id: landingContent?.usp_features?.centralized_management?.media?.id,
          className: '',
          media: {
            image: `${CDN_LINK}/seller/${landingContent?.usp_features?.centralized_management?.media?.image}`
          }
        }}
      />

      <MarketplaceFeatureSection
        id='6'
        className='onboard-once-feature '
        featureOverview={{
          className: '',
          label: landingContent?.usp_features?.onboard_once?.title,
          header: landingContent?.usp_features?.onboard_once?.header,
          content: landingContent?.usp_features?.onboard_once?.content
        }}
        mediaOverview={{
          id: landingContent?.usp_features?.onboard_once?.media?.id,
          className: '',
          media: {
            image: `${CDN_LINK}/seller/${landingContent?.usp_features?.onboard_once?.media?.image}`
          }
        }}
      />

      <Overview
        id='7'
        className='brand-overview'
        header={landingContent?.brand_about_us?.title}
        description={landingContent?.brand_about_us?.description}
        media={{
          image: `${CDN_LINK}/seller/${landingContent?.brand_about_us?.media?.image}`
        }}
        button={{
          type: landingContent?.brand_about_us?.button?.type,
          name: landingContent?.brand_about_us?.button?.name,
          handleButtonClick: () => handleScrollToContactUs()
        }}
      />

      <Feature
        className='partner-overview-heading'
        ref={partnersRef}
        overview={{
          header: landingContent?.partners?.title
        }}
      />
      <PartnerOverview
        id='8'
        className='partner-overview'
        logos={landingContent?.partners?.logos?.map((logo) => ({
          ...logo,
          image: `${CDN_LINK}/seller/${logo.image}`
        }))}
      />

      <Overview
        id='9'
        className='mobility-overview'
        header={landingContent?.mps_about_us?.title}
        headerStyles={landingContent?.mps_about_us?.headerStyles}
        description={landingContent?.mps_about_us?.description}
        media={{
          image: `${CDN_LINK}/seller/${landingContent?.mps_about_us?.media?.image}`
        }}
        button={{
          type: landingContent?.mps_about_us?.button?.type,
          name: landingContent?.mps_about_us?.button?.name,
          handleButtonClick: () => handleMpsButtonClick()
        }}
      />

      <GetInTouchSection
        landingContent={landingContent}
        contactUsRef={contactUsRef}
      />
      <MinimalFooter
        className='footer-component'
        footerTabs={updatedFooterTabs}
        entityName={entityName}
      />
      {scrollToTop && (
        <button
          className='scroll-to-top'
          onClick={handleScrollToTop}
          aria-label='Scroll to top'
        >
          <i className='ui-ic-up'></i>
        </button>
      )}
    </div>
  );
};

export default Landing;
